import merge from "lodash/merge"
import { DeepPartial, ISeriesApi, LogicalRange, SeriesOptionsCommon, WhitespaceData } from "lightweight-charts";
import Chart from "../chart";
import { ChartCandle, Series, SeriesGroup } from "../chart.typings";

class BaseChartSeries<D extends WhitespaceData = any, O = DeepPartial<SeriesOptionsCommon>> {
    data: D[] = [];
    rawData: ChartCandle[] = [];
    options: O; // raw options as passed to class
    seriesOptions: O;
    config: Series;
    seriesGroup: SeriesGroup
    chart: Chart;
    paneIndex: number;
    seriesApi: ISeriesApi<any>;
    metadata: Record<string, any> = {};

    constructor(chart: Chart, config: Series, seriesGroup: SeriesGroup, paneIndex: number, options: O) {
        this.options = options
        this.chart = chart;
        this.config = config;
        this.seriesGroup = seriesGroup;
        this.paneIndex = paneIndex
        this.seriesOptions = this.createSeriesOptions()
        this.init();
        this.seriesApi = this.addSeriesToChart()
    }

    init() { }
    addSeriesToChart(): ISeriesApi<any> {
        throw "Method not implemented"
    }
    _setData(data: ChartCandle[], metadata?: Record<string, any>) {
        this.metadata = metadata || {};
        this.setData(data, metadata)
        this.seriesApi.setData(this.data)
    }
    setData(data: ChartCandle[], metadata?: Record<string, any>): void {
        this.rawData = data;
        this.data = this.generateSeriesData(data, metadata)
    }
    updateData(metadata: Record<string, any> = {}): void {
        const result = this.generateSeriesData(this.rawData.slice(-10), metadata, true)
        const lastCandle = this.data[this.data.length - 1];
        const updatedCandle = result[result.length - 1];
        if (lastCandle && updatedCandle && lastCandle.time === updatedCandle.time) {
            this.data[this.data.length - 1] = updatedCandle
            return;
        }
        this.data.push(updatedCandle)
    }
    _update(candle: ChartCandle, metadata?: Record<string, any>) {
        this.metadata = metadata || {};
        const lastCandle = this.rawData[this.rawData.length - 1];
        if (lastCandle && lastCandle.time === candle.time) {
            this.rawData[this.rawData.length - 1] = candle
        } else {
            this.rawData.push(candle);
        }
        this.updateData(metadata)
        this.seriesApi.update(this.data[this.data.length - 1])
    };
    getBarsInfo(range: LogicalRange | null) {
        if (!range) return
        return this.seriesApi.barsInLogicalRange(range)
    }
    updateConfig(config: Partial<Series>) {


        if (config.chartType !== this.config.chartType) {
            this.chart.recreateSeries(this.seriesGroup.id, this.config.id)
            return;
        }
        const oldConfig = { ...this.config };
        this.config = { ...this.config, ...config }
        this.seriesOptions = this.createSeriesOptions()
        this.seriesApi.applyOptions(this.seriesOptions)
        this.onConfigUpdate(oldConfig)
    }

    updateOptions(options: DeepPartial<O>) {
        this.options = { ...this.options, ...options };
        this.config = { ...this.config, options }
        this.seriesOptions = this.createSeriesOptions()
        this.seriesApi.applyOptions(this.seriesOptions)
        this.onOptionsUpdate(options)
        this.setOptionsInChartConfig(options)
    }

    removeOptions() {
        this.options = {} as any
        this.config.options = {} as any
        this.seriesOptions = this.createSeriesOptions()
        this.seriesApi.applyOptions(this.seriesOptions)
        this.setOptionsInChartConfig({}, true)
    }

    onOptionsUpdate(options: DeepPartial<O>) { }
    setOptionsInChartConfig(options: DeepPartial<O>, removeOptions = false) {
        const seriesId = this.config.id
        if (!seriesId) return
        const newConfig = {
            ...this.chart.chartConfig
        }
        newConfig.series = {
            ...newConfig.series,
            [seriesId]: {
                ...newConfig.series[seriesId],
                options: removeOptions ? {} : {
                    ...newConfig.series[seriesId]?.options,
                    ...options
                }
            }
        }
        this.chart.updateChartConfig(newConfig, false)
        this.chart.callbacks.onConfigUpdate(newConfig)
    }
    onConfigUpdate(oldConfig: Series) { }
    createSeriesOptions(): O {
        const { thickness = 1, color, options = {} } = this.config;
        return merge({}, this.getDefaultOptions(), {
            color,
            thickness: thickness * devicePixelRatio,
            ...options
        } as O,)
    }

    getDefaultOptions(): O { return this.options }

    getData(): D[] {
        return this.data
    }

    getRawData(length = 0): ChartCandle[] {
        return length > 0 ? this.rawData.slice(-length) : this.rawData;
    }
    // Data after series type specific modifications like in case of Kagi/Renko etc. Length 0 means all candles
    getPrimaryData(length = 0): ChartCandle[] {
        return length > 0 ? this.rawData.slice(-length) : this.rawData;
    }

    generateSeriesData(data: ChartCandle[], metadata: Record<string, any> = {}, update = false): D[] {
        return data as unknown as D[];
    }
    destroy() {
        this.data = [];
        this.rawData = [];
        this.chart.drawingManager.removeAllDrawingsForSeries(this.config.id)
        this.chart.chartApi.removeSeries(this.seriesApi)
    };

}

export default BaseChartSeries;